import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// ── Typing Animation Hook ──
function useTypingAnimation(phrases: string[], speed = 80, pause = 2000) {
  const [displayText, setDisplayText] = useState('');
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const current = phrases[phraseIndex];
    let timeout: ReturnType<typeof setTimeout>;

    if (!isDeleting) {
      if (displayText.length < current.length) {
        timeout = setTimeout(() => {
          setDisplayText(current.slice(0, displayText.length + 1));
        }, speed);
      } else {
        timeout = setTimeout(() => setIsDeleting(true), pause);
      }
    } else {
      if (displayText.length > 0) {
        timeout = setTimeout(() => {
          setDisplayText(displayText.slice(0, -1));
        }, speed / 2);
      } else {
        setIsDeleting(false);
        setPhraseIndex((i) => (i + 1) % phrases.length);
      }
    }
    return () => clearTimeout(timeout);
  }, [displayText, isDeleting, phraseIndex, phrases, speed, pause]);

  return displayText;
}

// ── Terminal Component ──
const TerminalComponent: React.FC = () => {
  const [lines, setLines] = useState<string[]>([]);
  const terminalLines = [
    { text: '> Initializing security scan...', delay: 300, color: 'text-gray-400' },
    { text: '> Scanning network vulnerabilities...', delay: 1200, color: 'text-yellow-400' },
    { text: '> [OK] No critical threats detected', delay: 2500, color: '#00ff88' },
    { text: '> Loading profile: Tobi.exe', delay: 3400, color: 'text-gray-400' },
    { text: '> Access GRANTED. Welcome.', delay: 4500, color: '#00d4ff' },
  ];

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    terminalLines.forEach((line, i) => {
      const t = setTimeout(() => {
        setLines(prev => [...prev, JSON.stringify({ text: line.text, color: line.color })]);
      }, line.delay);
      timers.push(t);
    });
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div className="terminal-window max-w-md w-full">
      <div className="terminal-titlebar">
        <div className="terminal-dot" style={{ background: '#ff5f57' }} />
        <div className="terminal-dot" style={{ background: '#ffbd2e' }} />
        <div className="terminal-dot" style={{ background: '#28ca41' }} />
        <span className="mono text-xs ml-4" style={{ color: 'rgba(0,255,136,0.4)' }}>darsh@Tobi:~$</span>
      </div>
      <div className="terminal-body">
        {lines.map((lineStr, i) => {
          const line = JSON.parse(lineStr);
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -5 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
              className="mono text-sm"
              style={{ color: line.color === '#00ff88' || line.color === '#00d4ff' ? line.color : undefined }}
            >
              {line.color !== '#00ff88' && line.color !== '#00d4ff' ? (
                <span className={line.color}>{line.text}</span>
              ) : (
                <span style={{ color: line.color, textShadow: `0 0 10px ${line.color}` }}>{line.text}</span>
              )}
            </motion.div>
          );
        })}
        {lines.length < 5 && (
          <span className="mono text-sm" style={{ color: '#00ff88' }}>
            {'>'}  <span className="cursor-blink" />
          </span>
        )}
      </div>
    </div>
  );
};

// ── Main Hero ──
const Hero: React.FC = () => {
  const [nameIndex, setNameIndex] = useState(0);
  const names = ['Darshan', 'Tobi'];

  useEffect(() => {
    const interval = setInterval(() => {
      setNameIndex(prev => (prev + 1) % 2);
    }, 3500); // Toggles every 3.5 seconds
    return () => clearInterval(interval);
  }, []);

  const typingPhrases = [
    'Cybersecurity Enthusiast',
    'Web Exploitation (Learning)',
    'A Developer',
    'Network Security Analyst',
  ];
  const displayText = useTypingAnimation(typingPhrases, 75, 2200);



  const scrollToProjects = () => {
    const el = document.getElementById('projects');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      className="relative min-h-screen flex items-center pt-28 pb-20 overflow-hidden cyber-grid-bg"
      style={{ background: 'var(--bg-primary)' }}
    >
      {/* Animated Background Orbs */}
      <div className="bg-orb w-[600px] h-[600px] top-[-200px] right-[-100px]"
        style={{ background: 'radial-gradient(circle, rgba(0,255,136,0.06) 0%, transparent 70%)' }} />
      <div className="bg-orb w-[400px] h-[400px] bottom-[-100px] left-[-100px]"
        style={{ background: 'radial-gradient(circle, rgba(0,212,255,0.05) 0%, transparent 70%)', animationDelay: '-5s' }} />

      <div className="max-w-7xl mx-auto px-6 w-full relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">

          {/* ── Left: Text Content ── */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="space-y-8"
          >
            {/* Status Badge */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-3 px-4 py-2 rounded-full"
              style={{ background: 'rgba(0,255,136,0.08)', border: '1px solid rgba(0,255,136,0.2)' }}
            >
              <span className="status-dot" />
              <span className="mono text-xs" style={{ color: 'var(--neon-green)' }}>
                Available for Opportunities
              </span>
            </motion.div>

            {/* Main Heading */}
            <div className="space-y-2">
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="mono text-sm"
                style={{ color: 'var(--text-muted)' }}
              >
                // Hello, World
              </motion.p>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.8 }}
                className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black tracking-tighter leading-none flex flex-col items-start gap-2"
              >
                <span style={{ color: 'var(--text-primary)' }}>Hi, I'm</span>
                <span style={{ minWidth: '8ch', display: 'inline-block' }}>
                  <motion.span
                    key={nameIndex}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [0, 1, 0, 1] }}
                    transition={{ duration: 0.15, times: [0, 0.4, 0.8, 1], ease: "linear" }}
                    className="gradient-text-cyber glitch-text inline-block"
                    data-text={names[nameIndex]}
                  >
                    {names[nameIndex]}
                  </motion.span>
                </span>
              </motion.h1>

              {/* Typing Animation */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
                className="flex items-center gap-2 mt-4"
              >
                <span className="mono text-lg sm:text-xl md:text-2xl font-medium" style={{ color: 'var(--neon-cyan)' }}>
                  {displayText}
                </span>
                <span className="cursor-blink" style={{ background: 'var(--neon-cyan)' }} />
              </motion.div>
            </div>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="text-lg md:text-xl font-medium leading-relaxed max-w-lg"
              style={{ color: 'var(--text-secondary)' }}
            >
              I explore system vulnerabilities and build secure solutions.
              Interested in <span style={{ color: 'var(--neon-green)' }}>offensive</span> and{' '}
              <span style={{ color: 'var(--neon-cyan)' }}>defensive security</span>,
              network analysis, and writing clean Oh Srry I Mean Check And Bypass, Clean code.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.75 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <button
                id="hero-view-projects"
                onClick={scrollToProjects}
                className="btn-cyber-primary"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
                View Projects
              </button>

              <a
                id="hero-download-resume"
                href={`${import.meta.env.BASE_URL || '/'}Darshan-Resume.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-cyber-secondary flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download Resume
              </a>
            </motion.div>

            {/* Core Tags */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
              className="flex flex-wrap gap-2 pt-4"
              style={{ borderTop: '1px solid var(--border-subtle)' }}
            >
              {['Network Security', 'Arch Linux', 'Ethical Hacking', 'Python', 'Scripts,Bash', 'WIFI-BLE Attacks'].map(tag => (
                <span key={tag} className="tech-tag">{tag}</span>
              ))}
            </motion.div>
          </motion.div>

          {/* ── Right: Photo + Terminal ── */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
            className="flex flex-col items-center gap-8"
          >
            {/* Profile Photo */}
            <div className="relative animate-float">
              {/* Outer glow ring */}
              <div
                className="absolute -inset-4 rounded-[28px] blur-xl opacity-40"
                style={{ background: 'linear-gradient(135deg, var(--neon-green), var(--neon-cyan))' }}
              />
              <div className="photo-glow-frame relative">
                <img
                  src="./images/darshan.jpg"
                  alt="Darshan — Cybersecurity Enthusiast"
                  className="w-56 h-56 sm:w-64 sm:h-64 md:w-80 md:h-80 object-cover object-top"
                  style={{ borderRadius: '17px' }}
                  onError={(e) => {
                    // Fallback avatar if photo not found
                    const target = e.currentTarget;
                    target.style.display = 'none';
                    const parent = target.parentElement;
                    if (parent) {
                      const fallback = document.createElement('div');
                      fallback.style.cssText = `width: 320px; height: 320px; border-radius: 17px; background: var(--bg-elevated); display: flex; align-items: center; justify-content: center; font-size: 80px; font-weight: 900; color: var(--neon-green); font-family: 'JetBrains Mono', monospace;`;
                      fallback.textContent = 'D';
                      parent.appendChild(fallback);
                    }
                  }}
                />
              </div>

              {/* Status card */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.2 }}
                className="absolute -bottom-4 left-0 md:-left-6 px-4 py-3 rounded-xl"
                style={{
                  background: 'var(--bg-card)',
                  border: '1px solid rgba(0,255,136,0.3)',
                  boxShadow: '0 0 20px rgba(0,255,136,0.15)'
                }}
              >
                <div className="flex items-center gap-2 mono text-xs" style={{ color: 'var(--text-muted)' }}>
                  <span className="status-dot" />
                  <span>Open to Learn</span>
                </div>
                <div className="mono text-sm font-bold mt-1" style={{ color: 'var(--neon-green)' }}>
                  Security Research
                </div>
              </motion.div>

              {/* Skill badge */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.4 }}
                className="absolute -top-4 right-0 md:-right-6 px-4 py-3 rounded-xl"
                style={{
                  background: 'var(--bg-card)',
                  border: '1px solid rgba(0,212,255,0.3)',
                  boxShadow: '0 0 20px rgba(0,212,255,0.15)'
                }}
              >
                <div className="mono text-xs" style={{ color: 'var(--text-muted)' }}>Currently Learning</div>
                <div className="mono text-sm font-bold mt-1" style={{ color: 'var(--neon-cyan)' }}>
                  🔐 Pen Testing
                </div>
              </motion.div>
            </div>

            {/* Terminal Window */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="w-full max-w-md"
            >
              <TerminalComponent />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
